/* eslint-disable no-param-reassign */
import React from 'react';
import { loggingException } from 'app/libs/sentry';
import { goErrorPage } from 'app/libs/location';
import LoadingBar from 'app/components/common/LoadingBar';

// 청크 로드 실패 판별. lazyload가 던지는 ChunkLoadError + vite/브라우저의 동적 import 실패 메시지 포함.
const isLoadingChunkErrorTest = (error) =>
	error?.name === 'ChunkLoadError' ||
	/Loading.+?chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(error?.message);

const IS_RELOADED_PAGE = 'IS_RELOADED_PAGE';

class ErrorBoundary extends React.Component {
	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	static handleLoggingException(error, message) {
		const captureMessage = message ? `ErrorBoundary: ${message}` : `ErrorBoundary: ${error.message}`;

		loggingException(error, { captureMessage });
	}

	static handleGoToErrorPage(error, message) {
		if (process.env.NODE_ENV === 'production') {
			ErrorBoundary.handleLoggingException(error, message);

			setTimeout(() => goErrorPage(), 1000);
		}
	}

	static handleReloadApp() {
		window.location.reload();
	}

	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null };
		this.hasSessionStorage = Boolean(typeof window !== 'undefined' && window.sessionStorage);
	}

	componentDidCatch(error, info) {
		const { hasError } = this.state;
		const isLoadingChunkError = isLoadingChunkErrorTest(error);

		if (hasError) {
			if (isLoadingChunkError) {
				this.handleLoadingChunkError();

				return;
			}

			error.componentStack = info.componentStack;

			ErrorBoundary.handleGoToErrorPage(error);
		}
	}

	handleLoadingChunkError() {
		const isReloadedPage = this.getSessionStorage(IS_RELOADED_PAGE);

		if (isReloadedPage) {
			// 이미 1회 새로고침했는데도 실패 → 배포 갱신으로 해결되는 케이스가 아님(예: 사내 방화벽).
			// 에러페이지로 보내지 않고 로그만 남긴 뒤 현재 화면(render의 LoadingBar)에 머무른다.
			const { error } = this.state;
			this.setSessionStorage(IS_RELOADED_PAGE, false); // 플래그 리셋 → 다음 이동에서 다시 1회 재시도 가능
			ErrorBoundary.handleLoggingException(error, 'Chunk load 실패 — 화면 유지(에러페이지 이동 안 함)');
		} else {
			// 첫 실패 → 배포로 청크명이 바뀐(stale) 케이스일 수 있으니 1회 새로고침으로 회복을 시도한다.
			this.setSessionStorage(IS_RELOADED_PAGE, true);
			ErrorBoundary.handleReloadApp();
		}
	}

	getSessionStorage(key) {
		if (this.hasSessionStorage) {
			return JSON.parse(window.sessionStorage.getItem(key));
		}

		return null;
	}

	setSessionStorage(key, value) {
		if (this.hasSessionStorage) {
			window.sessionStorage.setItem(key, value);
		}
	}

	render() {
		const { hasError } = this.state;

		if (hasError) {
			return <LoadingBar />;
		}

		const { children } = this.props;
		return children;
	}
}

ErrorBoundary.defaultProps = {
	children: null,
};

export default ErrorBoundary;
